import { useState } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { UserCredential } from "@/types/user";

export interface UpdateUserCredentialData {
  credential_id: UserCredential["credential"]["id"];
  is_student?: UserCredential["is_student"];
  is_currently_active?: UserCredential["is_currently_active"];
  remove_credential?: boolean;
}

export interface UpdateUserCredentialResponse {
  success: boolean;
  message: string;
  credential?: {
    id: UserCredential["id"];
    is_student: UserCredential["is_student"];
    is_currently_active: UserCredential["is_currently_active"];
    credential: UserCredential;
    user: UserCredential["user"];
  };
}

export const useUpdateUserCredential = () => {
  const [data, setData] = useState<UpdateUserCredentialResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const updateUserCredential = async (formData: UpdateUserCredentialData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("Not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post<UpdateUserCredentialResponse>(
        "users/users/update_user_credential/",
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );
      setData(response.data);

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, success, updateUserCredential };
};
